import './InputGroup.css'
import cx from 'classnames'
import { DetailedHTMLProps, InputHTMLAttributes } from 'react'
import { NumericFormat, NumericFormatProps } from 'react-number-format'
import { Label } from '../InputLabel/InputLabel'
import { Input } from '../Input/Input'
import { InputSize } from '../Input/Input'
import { TriangleExclamationIcon } from '../icons'
import { Tooltip } from '../Tooltip/Tooltip'

export type InputVariant = 'default' | 'error'

export type InputProps = DetailedHTMLProps<
  InputHTMLAttributes<HTMLInputElement>,
  HTMLInputElement
> & {
  placeholder?: string
  label?: React.ReactNode
  suffix?: React.ReactNode
  sizeType?: InputSize
  hint?: React.ReactNode
  variant?: InputVariant
  error?: string
  warning?: string
  tooltip?: React.ReactNode
  tooltipPlacement?: 'top' | 'bottom'
  tooltipSize?: 'small' | 'medium' | 'large'
  value?: string
}

export function Suffix({ suffix }: { suffix: React.ReactNode }) {
  return <div className={cx('input-suffix', 'input-suffix-validation')}>{suffix}</div>
}

function GenericInputGroup({
  id,
  label,
  value,
  disabled,
  onChange,
  onBlur,
  placeholder,
  suffix,
  type,
  name,
  sizeType = 'md',
  hint,
  variant = 'default',
  min,
  max,
  error,
  required,
  tooltip,
  tooltipPlacement = 'top',
  tooltipSize = 'small',
  warning,
  renderInput
}: InputProps & {
  renderInput: (props: InputProps) => React.ReactNode
}) {
  const hasError = !!error
  const hasWarning = !!warning

  return (
    <div>
      {/* ─ Label + info-tooltip ─ */}
      {label && (
        <Label
          className="mb-1.5 inline-flex items-center gap-1"
          required={required}
          tooltip={tooltip}
          tooltipPlacement={tooltipPlacement}
          tooltipSize={tooltipSize}
          sizeType="sm"
          htmlFor={id}
        >
          {label}
        </Label>
      )}

      {/* ─ Input row ─ */}
      <div
        className={cx('input-group-wrapper group', {
          suffix: !!suffix,
          invalid: hasError,
          warning: hasWarning,
          'input-group-invalid': hasError,
          'input-group-warning': hasWarning,
          'input-group-default': !hasError && !hasWarning
        })}
      >
        <div className="input-inner-wrapper">
          {renderInput({
            className: cx('input-with-suffix', 'input-field-invalid', 'input-field-warning'),
            id,
            type,
            value,
            placeholder,
            name,
            disabled,
            onChange,
            onBlur,
            sizeType,
            variant,
            min,
            max
          })}

          {/* error / warning icon with tooltip */}
          {(hasError || hasWarning) && (
            <div className="input-validation-icon-container">
              <Tooltip
                content={error || warning}
                variant={error ? 'danger' : 'warning'}
                position="top"
              >
                <TriangleExclamationIcon
                  className={cx('w-4 cursor-help', {
                    'text-red-500': hasError,
                    'text-yellow-500': hasWarning
                  })}
                />
              </Tooltip>
            </div>
          )}
        </div>

        {suffix && <Suffix suffix={suffix} />}
      </div>

      {hint && <label className="input-hint">{hint}</label>}
    </div>
  )
}

export function InputGroup(props: InputProps) {
  return (
    <GenericInputGroup
      {...props}
      renderInput={({
        className,
        id,
        type,
        value,
        placeholder,
        name,
        disabled,
        onChange,
        onBlur,
        sizeType
      }) => (
        <Input
          className={className}
          id={id}
          type={type}
          value={value}
          placeholder={placeholder}
          name={name}
          disabled={disabled}
          onChange={onChange}
          onBlur={onBlur}
          sizeType={sizeType}
        />
      )}
    />
  )
}

export function NumericInputGroup({
  numericFormat,
  ...props
}: InputProps & { numericFormat?: NumericFormatProps }) {
  return (
    <GenericInputGroup
      {...props}
      renderInput={({
        className,
        id,
        type,
        value,
        placeholder,
        name,
        disabled,
        onChange,
        sizeType
      }) => (
        <NumericFormat
          {...numericFormat}
          className={className}
          id={id}
          type={type as 'text' | 'tel' | 'password'}
          value={value}
          placeholder={placeholder}
          name={name}
          disabled={disabled}
          onChange={onChange}
          sizeType={sizeType}
          customInput={Input}
        />
      )}
    />
  )
}
